import React, { useEffect } from 'react';
import { connect } from 'react-redux';

import Wizard from '../containers/Modal/Wizard';
import Strings from '../Strings';
import { Input, SelectField } from '../containers/Form';
import { notificationActions } from '../components/Notification/redux/actions';
import { closeModal } from '../actions/modal';
import { actions } from '../pages/Admin/Users/redux/actions';
import { actions as orgActions } from '../pages/Organizations/redux/actions';
import { CREATE_ADMIN_ROLE } from '../pages/Admin/Users/redux/constants';
import LoadingRenderer from 'components/LoadingRenderer';

function CreateRoleModal(props) {
  const { getArchetypes, getOrganizations, createRole, showNotification, closeModalWithNextAction } = props;

  const [role, setRole] = React.useState({ title: '', archetype_id: '', org_uuid: '' });
  const [archetypes, setArchetypes] = React.useState([]);
  const [organizations, setOrganizations] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState([]);

    // Load dropdown data
  React.useEffect(() => {
    setIsLoading(true);
    const loadDropdownData = async () => {
      try {
        const [archetypesResp, orgsResp] = await Promise.all([
          getArchetypes ? getArchetypes({ limit: 1000, offset: 0 }) : Promise.resolve(null),
          getOrganizations ? getOrganizations({ limit: 1000, offset: 0 }) : Promise.resolve(null),
        ]);

        const archetypesData = Array.isArray(archetypesResp?.response)
          ? archetypesResp.response
          : archetypesResp?.response?.data || [];

        if (archetypesData.length > 0) {
          setArchetypes(archetypesData);
        }
        if (orgsResp?.response?.data) {
          setOrganizations(orgsResp.response.data);
        }
      } catch (err) {
        console.error('Failed to load dropdown data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadDropdownData();
  }, [getArchetypes, getOrganizations]);

  useEffect(() => {
    const errors = [];
    if (!role.title) {
      errors.push({ property: 'title', propertyName: Strings.title, missing: true, errors: [] });
    }

    if (!role.archetype_id) {
      errors.push({ property: 'archetype_id', propertyName: Strings.archetype, missing: true, errors: [] });
    }
    if (!role.org_uuid) {
      errors.push({ property: 'org_uuid', propertyName: Strings.organization, missing: true, errors: [] });
    }
    setValidationErrors(errors);
  }, [role]);

  const onSubmit = () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    createRole(role).then(response => {
      if (response) {
        if (response.type === `${CREATE_ADMIN_ROLE}/error`) {
          const error = response.response?.data?.standard_error?.message;
          showNotification(error, 5000, true);
        } else {
          showNotification(Strings.formatString(Strings.userSaved, role.title));
          closeModalWithNextAction();
        }
      }
      return response;
    });
    setIsSaving(false);
  };

  const onDataChange = (name, value) => {
    setRole(r => ({
      ...r,
      [name]: value,
    }));
    if (validationErrors.find(v => v.property === name)) {
      setValidationErrors(errors => errors.filter(v => v.property !== name));
    }
  };

  const pages = [
    {
      id: 'create-role-page',
      title: Strings.createRole || 'Create Role',
      content: (
        <LoadingRenderer loading={isLoading}>
          <Input
            name="title"
            id="title"
            label={Strings.title}
            type="text"
            value={role.title || ''}
            onChange={e => onDataChange('title', e.target.value)}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'title')?.errors || []}
            data-testid="role-title"
          />
          {archetypes.length > 1 && (
            <SelectField
              name="archetype_id"
              id="archetype_id"
              label={Strings.archetype}
              value={role.archetype_id}
              onChange={o => onDataChange('archetype_id', o.value)}
              data={archetypes.map(a => ({ ...a, value: a.id, label: a.role_name }))}
              isRequired
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(v => v.property === 'archetype_id')?.errors || []}
            />
          )}
          {organizations.length > 1 && (
            <SelectField
              name="org_uuid"
              id="org_uuid"
              label={Strings.organization}
              value={role.org_uuid}
              onChange={o => onDataChange('org_uuid', o.value)}
              data={organizations.map(o => ({ ...o, value: o.masked_id, label: o.name })) || []}
              isSearchable
              isRequired
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(v => v.property === 'org_uuid')?.errors || []}
            />
          )}
        </LoadingRenderer>
      ),
      emptyFieldsCount: validationErrors.filter(v => v.missing).length,
      canGoNext: validationErrors.length === 0 && !isSaving,
    },
  ];

  return (
    <Wizard
      name="create-role"
      pages={pages}
      onNextButtonHover={e => setHighlightMissingFields(e)}
      onSubmit={onSubmit}
      showPagesFilter={false}
    />
  );
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getRoleDetails: roleId => dispatch(actions.getRoleDetails(roleId)),
  getArchetypes: params => dispatch(actions.getArchetypes(params)),
  getOrganizations: params => dispatch(orgActions.queryOrganizations(params)),
  createRole: data => dispatch(actions.createRole(data)),
  showNotification: (message, timeout) => dispatch(notificationActions.show(message, timeout)),
  closeModalWithNextAction: () => {
    dispatch(closeModal('create-role'));
    if (ownProps.data?.onSuccess) ownProps.data.onSuccess();
  },
});

export default connect(null, mapDispatchToProps)(CreateRoleModal);
